/**
 * @fileoverview Copy to clipboard: [vdx-copy-button] copies content from [vdx-copy-this] and optionally shows [vdx-copy-alert].
 */

import { handleError } from './errors.js';

const MODULE = 'copyclip';

const SELECTOR_BUTTON = '[vdx-copy-button]';
const SELECTOR_THIS = '[vdx-copy-this]';
const SELECTOR_ALERT = '[vdx-copy-alert]';
const ATTR_BUTTON = 'vdx-copy-button';
const ATTR_THIS = 'vdx-copy-this';
const ATTR_ALERT = 'vdx-copy-alert';

/** How long the success alert is shown (ms) before hiding. */
const ALERT_DURATION_MS = 2000;

/** Attribute set when a copy button has been wired to avoid duplicate listeners. */
const WIRED_ATTR = 'vdx-wired';

/**
 * Hides all [vdx-copy-alert] elements.
 */
function hideAllAlerts() {
    if (typeof document === 'undefined') return;
    document.querySelectorAll(SELECTOR_ALERT).forEach((el) => {
        if (el && el.style) el.style.display = 'none';
    });
}

/**
 * Shows the given alert (or default) and hides it after ALERT_DURATION_MS.
 * @param {Element | null} alertElement - Alert for this copy pair (vdx-copy-alert="${id}").
 * @param {Element | null} defaultAlert - Fallback alert (vdx-copy-alert="").
 */
function showAlert(alertElement, defaultAlert) {
    if (alertElement && alertElement.style) alertElement.style.display = 'block';
    else if (defaultAlert && defaultAlert.style) defaultAlert.style.display = 'block';
    setTimeout(() => {
        if (alertElement && alertElement.style) alertElement.style.display = 'none';
        if (defaultAlert && defaultAlert.style) defaultAlert.style.display = 'none';
    }, ALERT_DURATION_MS);
}

/**
 * Returns the text to copy from the target element (handles href mailto/tel/# and plain text).
 * @param {Element} targetElement - Element with [vdx-copy-this].
 * @param {Event} event - Click event (preventDefault may be called for mailto/tel/#).
 * @returns {string}
 */
function getTextToCopy(targetElement, event) {
    if (targetElement.hasAttribute && targetElement.hasAttribute('href')) {
        const href = targetElement.getAttribute('href') || '';
        if (href.startsWith('mailto:')) {
            if (event && event.preventDefault) event.preventDefault();
            return href.replace('mailto:', '').trim();
        }
        if (href.startsWith('tel:')) {
            if (event && event.preventDefault) event.preventDefault();
            return href.replace('tel:', '').trim();
        }
        if (href === '#') {
            if (event && event.preventDefault) event.preventDefault();
            return typeof window !== 'undefined' && window.location
                ? window.location.href.split('#')[0]
                : '';
        }
        try {
            return typeof window !== 'undefined' && window.location
                ? new URL(href, window.location.origin).href
                : href;
        } catch {
            return href;
        }
    }
    const text = targetElement.textContent ?? targetElement.innerText ?? '';
    return String(text).trim();
}

/**
 * Wires one copy button: click copies target content to clipboard and shows alert on success.
 * @param {Element} button - Element with [vdx-copy-button].
 */
function wireButton(button) {
    if (button.hasAttribute(WIRED_ATTR)) return;
    button.setAttribute(WIRED_ATTR, '');

    const targetId = button.getAttribute(ATTR_BUTTON);
    const targetElement = targetId ? document.querySelector(`[${ATTR_THIS}="${targetId}"]`) : null;
    const alertElement = targetId ? document.querySelector(`[${ATTR_ALERT}="${targetId}"]`) : null;
    const defaultAlert = document.querySelector(`[${ATTR_ALERT}=""]`);

    if (!targetElement) {
        const msg = targetId
            ? `vdx-copy-button value "${targetId}" has no matching [vdx-copy-this="${targetId}"]. Add an element with vdx-copy-this="${targetId}".`
            : 'vdx-copy-button attribute is missing or empty. Set vdx-copy-button to the id of the element to copy (with vdx-copy-this).';
        handleError(msg, { module: MODULE, element: button, showOnElement: true });
        return;
    }

    button.addEventListener('click', (event) => {
        const textToCopy = getTextToCopy(targetElement, event);
        if (typeof navigator === 'undefined' || !navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
            handleError('Clipboard API not available (HTTPS or secure context required).', {
                module: MODULE,
                element: button,
                showOnElement: true,
            });
            return;
        }
        navigator.clipboard
            .writeText(textToCopy)
            .then(() => showAlert(alertElement, defaultAlert))
            .catch((err) => {
                handleError('Clipboard write failed (permission or unsupported). Check [vdx-copy-button] and [vdx-copy-this] target.', {
                    module: MODULE,
                    element: button,
                    showOnElement: true,
                    error: err,
                });
            });
    });
}

/**
 * Reports orphan [vdx-copy-this] elements (no matching [vdx-copy-button]).
 */
function reportOrphanTargets() {
    if (typeof document === 'undefined') return;
    document.querySelectorAll(SELECTOR_THIS).forEach((targetElement) => {
        const targetId = targetElement.getAttribute(ATTR_THIS);
        if (document.querySelector(`[${ATTR_BUTTON}="${targetId}"]`)) return;
        const msg = targetId
            ? `vdx-copy-this value "${targetId}" has no matching [vdx-copy-button="${targetId}"]. Add a button with vdx-copy-button="${targetId}".`
            : 'vdx-copy-this attribute is missing or empty. Set vdx-copy-this to match a vdx-copy-button value.';
        handleError(msg, { module: MODULE, element: targetElement, showOnElement: true });
    });
}

/**
 * Initializes copy-to-clipboard: wires [vdx-copy-button] to copy from [vdx-copy-this] and show [vdx-copy-alert].
 * Safe to call multiple times (each button wired at most once).
 */
export function copyToClipboard() {
    if (typeof document === 'undefined') return;
    hideAllAlerts();
    document.querySelectorAll(SELECTOR_BUTTON).forEach(wireButton);
    reportOrphanTargets();
}
